/**
 * Geometria do sparkline: série → coordenadas dentro de um viewBox.
 *
 * ⚠️ Por que NÃO o `v-sparkline` do Vuetify: com `fill`, ele desenha só a área
 * preenchida e não traça a linha — as duas coisas são exclusivas naquele
 * componente (medido). O protótipo tem as DUAS: o traço verde e um degradê leve
 * saindo dele. Aqui a linha e a área são dois caminhos independentes.
 *
 * Puro e sem Vue de propósito: a matemática é o que erra em silêncio (série de
 * um ponto só, valores todos iguais, `null` no meio), e isso se testa direto.
 */

// Folga vertical para o traço não encostar nas bordas do viewBox.
const FOLGA = 3

/**
 * Só os números utilizáveis. Um `null` no meio da série viraria `NaN` na
 * coordenada e apagaria o caminho INTEIRO — não só aquele ponto.
 * @param {Array} valores
 * @returns {Array<number>}
 */
const numerosUteis = (valores) =>
  (valores || []).map(Number).filter((n) => isFinite(n))

/**
 * @param {Array} valores
 * @param {number} largura
 * @param {number} altura
 * @returns {Array<{x: number, y: number}>}
 */
export const pontosDoSparkline = (valores, largura, altura) => {
  const numeros = numerosUteis(valores)
  if (numeros.length < 2) return []

  const menor = Math.min.apply(null, numeros)
  const maior = Math.max.apply(null, numeros)
  // Série constante: sem amplitude, todo ponto cairia no mesmo lugar por
  // divisão por zero. Desenha no meio — é a leitura honesta de "não variou".
  const amplitude = maior - menor
  const alturaUtil = altura - FOLGA * 2
  const passo = largura / (numeros.length - 1)

  return numeros.map((valor, indice) => {
    const proporcao = amplitude === 0 ? 0.5 : (valor - menor) / amplitude
    return {
      x: Number((indice * passo).toFixed(2)),
      // SVG cresce para BAIXO: o maior valor tem o menor y.
      y: Number((FOLGA + (1 - proporcao) * alturaUtil).toFixed(2)),
    }
  })
}

/**
 * Caminho da LINHA (só o traço).
 * @returns {string} vazio quando não há série suficiente
 */
export const caminhoDaLinha = (valores, largura, altura) => {
  const pontos = pontosDoSparkline(valores, largura, altura)
  if (!pontos.length) return ''
  return pontos
    .map((p, i) => (i === 0 ? 'M' : 'L') + p.x + ' ' + p.y)
    .join(' ')
}

/**
 * Caminho da ÁREA: a mesma linha, fechada contra a base — é o que recebe o
 * degradê.
 * @returns {string} vazio quando não há série suficiente
 */
export const caminhoDaArea = (valores, largura, altura) => {
  const linha = caminhoDaLinha(valores, largura, altura)
  if (!linha) return ''
  return linha + ' L' + largura + ' ' + altura + ' L0 ' + altura + ' Z'
}
